import React, { useEffect } from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import { useAuthStore } from "../../@shared/store/useAuthStore";
import { Box } from "@mui/material";
import { normalizeAvatarName } from "../../@shared/utils/SpritesMap";
import { resolveAvatarData } from "../../@shared/utils/orbAvatar";

// Breathing configs
const torsoBreath = {
  animate: {
    scale: [1, 1.02, 1],
    y: [0, -2, 0],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

const delayedBreath = (delay = 0.1) => ({
  animate: {
    scale: [1, 1.02, 1],
    y: [0, -2, 0],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
      delay,
    },
  },
});

const headBreath = {
  animate: {
    y: [0, -2, 0],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

// Character asset maps
const avatarAssets = {
  zay: {
    segments: {
      legs: require("../../@shared/assets/avatars/zay/zay-idol-legs.png"),
      torso: require("../../@shared/assets/avatars/zay/zay-idol-torso.png"),
      eyes: require("../../@shared/assets/avatars/zay/zay-idol-eyes.png"),
      head: require("../../@shared/assets/avatars/zay/zay-idol-head.png"),
      armLeft: require("../../@shared/assets/avatars/zay/zay-idol-arm-left.png"),
      armRight: require("../../@shared/assets/avatars/zay/zay-idol-arm-right.png"),
    },
  },
  kolby: {
    segments: {
      legs: require("../../@shared/assets/avatars/kolby/kolby-idol-legs.png"),
      torso: require("../../@shared/assets/avatars/kolby/kolby-idol-torso.png"),
      eyes: require("../../@shared/assets/avatars/kolby/kolby-idol-eyes.png"),
      head: require("../../@shared/assets/avatars/kolby/kolby-idol-head.png"),
      armLeft: require("../../@shared/assets/avatars/kolby/kolby-idol-left-arm.png"),
      armRight: "",
    },
  },
  mei_mei: {
    segments: {
      legs: require("../../@shared/assets/avatars/meimei/mei-mei-legs.png"),
      torso: require("../../@shared/assets/avatars/meimei/mei-mei-torso.png"),
      eyes: require("../../@shared/assets/avatars/meimei/mei-mei-eyes.png"),
      head: require("../../@shared/assets/avatars/meimei/mei-mei-head.png"),
      armLeft: require("../../@shared/assets/avatars/meimei/mei-mei-left-arm.png"),
      armRight: "",
    },
  },
  yuna: {
    segments: {
      legs: require("../../@shared/assets/avatars/yuna/yuna-idol-legs.png"),
      torso: require("../../@shared/assets/avatars/yuna/yuna-idol-torso.png"),
      eyes: require("../../@shared/assets/avatars/yuna/yuna-idol-eyes.png"),
      head: require("../../@shared/assets/avatars/yuna/yuna-idol-head.png"),
      armLeft: "",
      armRight: "",
    },
  },
  io: {
    segments: {
      torso: "",
    },
  },
};

const AvatarIdolAnimation = ({
  name,
  style = {},
  onclick,
  avatarEntity,
  orbImageOffset = "0%",
}) => {
  // Infinity Orb animation
  const { userData } = useAuthStore();
  const avatarData = resolveAvatarData(avatarEntity || userData);
  const ySpring = useSpring(0, { stiffness: 60, damping: 15 });
  const ioShadowScale = useTransform(ySpring, [-5, 5], [0.82, 1.02]);

  const safeName = String(name || "");
  const isIO =
    avatarData.isOrbAvatar ||
    safeName.toLowerCase().includes("io") ||
    safeName.toLowerCase().includes("infinity") ||
    safeName.toLowerCase().includes("i.o") ||
    safeName.toLowerCase().includes("orb");

  useEffect(() => {
    if (isIO) {
      const loop = () => {
        ySpring.set(-5);
        setTimeout(() => ySpring.set(5), 500);
        setTimeout(() => ySpring.set(-5), 1000);
      };
      const interval = setInterval(loop, 1000);
      return () => clearInterval(interval);
    }
  }, [safeName, ySpring, avatarData.isOrbAvatar]);

  // Render segmented avatar
  const { segments } =
    avatarAssets[isIO ? "io" : normalizeAvatarName(safeName)] || avatarAssets.io;

  const segment = (key, motionProps, zIndex = 2) => (
    <motion.img
      key={key}
      src={segments[key]}
      alt={""}
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        zIndex,
        pointerEvents: "none",
        width: "100%",
      }}
      {...motionProps}
    />
  );

  return (
    <motion.div
      onClick={onclick}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
      style={{
        position: "relative",
        cursor: "pointer",
        width: "90%",
        height: "fit-content",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // marginTop: 50,
        ...style,
      }}
      >
      {isIO ? (
        <Box
          sx={{
            width: "100%",
            mb: orbImageOffset,
          }}
        >
          <motion.img
            style={{
              width: "100%",
              height: "auto",
              y: ySpring,
            }}
            src={avatarData?.image}
            alt=""
          />
        </Box>
      ) : (
        <>
          <img
            src={segments.legs}
            alt=""
            style={{
              //   position: "absolute",
              //   bottom: 0,
              //   left: "20%",
              width: "100%",
              zIndex: 1,
              pointerEvents: "none",
            }}
          />

          {segment("torso", torsoBreath, 2)}

          {segment(
            "eyes",
            {
              animate: { opacity: [0, 1, 0] },
              transition: {
                duration: 0.2,
                repeat: Infinity,
                repeatDelay: 4,
                ease: "easeInOut",
              },
            },
            4
          )}

          {segment("head", headBreath, 3)}
          {segment("armLeft", delayedBreath(0.1), 2)}
          {segment("armRight", delayedBreath(0.1), 2)}
        </>
      )}

      <Box
        component={motion.div}
        sx={{
          position: "absolute",
          bottom: "-1%",
          left: isIO ? "28%" : "48%",
          transform: "translate(-50%, 0%)",
          width: isIO ? "38%" : "50%",
          height: isIO ? { xs: "22px", md: "28px" } : { xs: "30px", md: "40px" },
          borderRadius: "50%",
          backgroundColor: "#00000090",
          filter: "blur(4px)",
        }}
        style={
          isIO
            ? {
                scaleX: ioShadowScale,
                scaleY: ioShadowScale,
              }
            : {left: "48%",}
        }
      />
    </motion.div>
  );
};

export default AvatarIdolAnimation;
